import { useEffect, useMemo, useRef, useState } from "react";
import type { MatchupReport, MatchupReportIndex, MatchupReportIndexEntry } from "@averagejoesff/shared";
import { fetchMatchupDraft } from "../../api/adminApi";
import { fetchMatchupIndex, fetchMatchupReport } from "../../api/awards";
import { useAuth } from "../../auth/AuthProvider";
import { ResourceStatus } from "../../components/ResourceStatus";
import { StateMessage } from "../../components/StateMessage";
import { Tabs } from "../../components/Tabs";
import { useResource } from "../../lib/useResource";
import { MatchupDraftPanel } from "../admin/MatchupDraftPanel";
import { UploadMatchupReportForm } from "../admin/UploadMatchupReportForm";
import { HeadToHeadExplorer } from "./HeadToHeadExplorer";
import { MatchupCard } from "./MatchupCard";
import { MatchupSlate } from "./MatchupSlate";
import { QuickHits } from "./QuickHits";
import { indexManagers } from "./managers";

type Tab = "week" | "h2h";

const reportKey = (r: { season: number; week: number }) => `${r.season}-${r.week}`;

/**
 * Site-wide (not tied to the season picker): each skill-authored report
 * carries its own season/week, so this view fetches the report index itself
 * and offers its own week picker.
 */
export function MatchupsView() {
  const { isAuthenticated, idToken } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [tab, setTab] = useState<Tab>("week");
  // A link from Home names its week (?week=2026-5), so it opens that report rather than whichever is newest by then.
  const [selectedKey, setSelectedKey] = useState<string | null>(() => new URLSearchParams(window.location.search).get("week"));
  const [index, setIndex] = useResource(fetchMatchupIndex, []);
  // A just-published report, shown directly rather than re-fetched: a read
  // this soon after the write could hit CloudFront's cached pre-write copy
  // (see useResource's doc comment).
  const [published, setPublished] = useState<MatchupReport | null>(null);
  const [previewingDraft, setPreviewingDraft] = useState(false);
  const [draft] = useResource(
    () => (idToken ? fetchMatchupDraft(idToken) : Promise.resolve(null)),
    [idToken],
  );

  const entries = index.status === "ready" ? (index.data?.reports ?? []) : [];
  const selected: MatchupReportIndexEntry | null =
    entries.find((e) => reportKey(e) === selectedKey) ?? entries[0] ?? null;
  const showPublished = published !== null && selected !== null && reportKey(published) === reportKey(selected);
  // A draft is only offered until its week shows up in the published index.
  const pendingDraft =
    draft.status === "ready" && draft.data && !entries.some((e) => reportKey(e) === reportKey(draft.data!.report))
      ? draft.data
      : null;

  const [fetched] = useResource(
    () =>
      selected === null || showPublished
        ? Promise.resolve(null)
        : fetchMatchupReport(selected.season, selected.week),
    [selected?.season, selected?.week, showPublished],
    // Moving between weeks keeps the last report up (dimmed) until the next arrives.
    { keepPrevious: true },
  );

  function handlePublished(result: { report: MatchupReport; index: MatchupReportIndex }) {
    setIndex(result.index);
    setPublished(result.report);
    setSelectedKey(reportKey(result.report));
    setEditMode(false);
    setPreviewingDraft(false);
  }

  /** The selected published week: its loading/error/empty states, or the report itself. */
  function renderPublished() {
    if (index.status !== "ready") {
      return <ResourceStatus resource={index} loading="Loading matchups…" error="Could not load matchups" />;
    }
    if (selected === null) {
      return (
        <StateMessage
          variant="empty"
          title="No matchup reports yet"
          detail="Check back once this week's report is published."
        />
      );
    }
    if (showPublished) return <ReportBody report={published} tab={tab} onTabChange={setTab} />;
    if (fetched.status !== "ready") {
      return <ResourceStatus resource={fetched} loading="Loading this week…" error="Could not load this report" />;
    }
    if (fetched.data === null) {
      return <StateMessage variant="empty" title={`No report for ${selected.season} week ${selected.week}`} />;
    }
    return (
      <div className={fetched.refreshing ? "is-refreshing" : undefined} aria-busy={fetched.refreshing}>
        <ReportBody report={fetched.data} tab={tab} onTabChange={setTab} />
      </div>
    );
  }

  return (
    <section className="feature" aria-labelledby="matchups-heading">
      <div className="feature__intro">
        <div className="feature__intro-row">
          <h2 id="matchups-heading" className="feature__title">
            Matchups
          </h2>
          {entries.length > 0 && selected && (
            <label className="season-picker">
              {/* Visually hidden: each option already reads "2026 · Week 4", and the
                  extra word pushed the picker off a 320px screen. */}
              <span className="season-picker__label visually-hidden">Week</span>
              <select
                className="season-picker__select"
                value={reportKey(selected)}
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                {entries.map((e) => (
                  <option key={reportKey(e)} value={reportKey(e)}>
                    {e.season} · Week {e.week}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <p className="feature__description">This week's games and the history behind every rivalry.</p>
      </div>

      {isAuthenticated && idToken && (
        <button type="button" className="feature__edit-toggle" onClick={() => setEditMode((v) => !v)}>
          {editMode ? "Done uploading" : "Upload report"}
        </button>
      )}
      {editMode && idToken && (
        <section className="admin-panel" aria-label="Upload a matchup report">
          <UploadMatchupReportForm idToken={idToken} onPublished={handlePublished} />
        </section>
      )}
      {pendingDraft && idToken && (
        <MatchupDraftPanel
          draft={pendingDraft}
          idToken={idToken}
          previewing={previewingDraft}
          onTogglePreview={() => setPreviewingDraft((v) => !v)}
          onPublished={handlePublished}
        />
      )}
      {pendingDraft && previewingDraft ? (
        <div className="matchup-draft__preview">
          <ReportBody report={pendingDraft.report} tab={tab} onTabChange={setTab} />
        </div>
      ) : (
        renderPublished()
      )}
    </section>
  );
}

interface ReportBodyProps {
  report: MatchupReport;
  tab: Tab;
  onTabChange: (tab: Tab) => void;
}

function ReportBody({ report, tab, onTabChange }: ReportBodyProps) {
  const managers = useMemo(() => indexManagers(report), [report]);

  // A link into one game (Home's slate, or a pasted URL) ends in `#matchup-…`, but its card only exists
  // once the report has loaded, so the browser's own jump has nothing to land on. Done once per visit:
  // picking another week afterwards must not yank the page back to a game from the old one. A link that
  // names a week (?week=) only jumps when that is the report on screen, so it never lands on another week's game.
  const jumped = useRef(false);
  useEffect(() => {
    if (jumped.current || tab !== "week") return;
    jumped.current = true;
    const week = new URLSearchParams(window.location.search).get("week");
    if (week && week !== reportKey(report)) return;
    const id = safeDecode(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView();
  }, [tab, report]);

  return (
    <>
      <Tabs
        tabs={[
          { id: "week", label: `Week ${report.week}` },
          { id: "h2h", label: "Head-to-head" },
        ]}
        active={tab}
        onChange={onTabChange}
        block="matchups"
      />

      {tab === "week" ? (
        <>
          <QuickHits hits={report.quickHits} managers={managers} />
          <MatchupSlate report={report} managers={managers} />
          <div className="matchups__cards">
            {report.matchups.map((matchup) => (
              <MatchupCard
                key={matchup.managerIds.join("-")}
                matchup={matchup}
                report={report}
                managers={managers}
              />
            ))}
          </div>
        </>
      ) : (
        // Keyed by report so switching weeks resets the explorer's picks.
        <HeadToHeadExplorer key={reportKey(report)} report={report} managers={managers} />
      )}
    </>
  );
}

/** A pasted link can carry a malformed escape (`#matchup-%`), which `decodeURIComponent` throws on. */
function safeDecode(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return "";
  }
}
